/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { useRollbar } from '@rollbar/react'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import Page from './elements/Page'
import EmptyState from './EmptyState'
import PaymongoCheckoutForm from './PaymongoCheckoutForm'
import RegistrationSummary from './RegistrationSummary'
import { PAYMENT_MODES, TEAM_TYPES } from '../constants/models'
import { EventWaiver } from '../constants/types'
import { CartContext } from '../contexts/CartContext'
import { PageLoaderContext } from '../contexts/PageLoaderContext'
import useCategoryFees from '../hooks/useCategoryFees'
import {
  PaymentIntentResponse,
  AttachPaymentIntentResponse,
  AttachPaymentIntentRequest,
} from '../models/Paymongo/PaymentIntent'
import { PaymentMethodResponse } from '../models/Paymongo/PaymentMethod'
import { generateCartItems } from '../utils/ApiHelpers'
import {
  createPaymentIntent,
  createPaymentMethod,
  attachToPaymentIntent,
  doCreatePendingOrder,
  doCreateFreeRegistration,
  fetchCategoryFees,
} from '../utils/PaymongoCheckoutUtils'

interface Props {
  show?: boolean
  onCancel?: React.MouseEventHandler<HTMLButtonElement>
  isVirtualEvent?: boolean
  eventWaiver?: EventWaiver | null
  isFree?: boolean
}

interface OngoingPaymentNoticeProps {
  isFree?: boolean
}
const OngoingPaymentNotice: React.FC<OngoingPaymentNoticeProps> = (isFree) => (
  <div
    style={{
      textAlign: 'center',
      padding: '19px',
      marginBottom: '30px',
      border: '2px solid red',
      color: 'black',
    }}>
    Please <b>DO NOT refresh</b> the page and wait while we are processing your
    {isFree ? ' submission' : ' payment'}.
  </div>
)

const PaymongoCheckout: React.FC<Props> = ({
  isVirtualEvent = false,
  eventWaiver = null,
  isFree = false,
  show = true,
  onCancel,
}) => {
  const rollbar = useRollbar()

  const { enqueueSnackbar } = useSnackbar()

  const [checkWaiver, setCheckWaiver] = useState(false)
  const [checkWaiverOrg, setCheckWaiverOrg] = useState(eventWaiver === null)
  const [checkAgreeVerify, setCheckAgreeVerify] = useState(isVirtualEvent)
  const { setLoading: setPageLoading, pageLoading } =
    useContext(PageLoaderContext)

  const [loading, setLoading] = useState(false)
  // const [shippingPreference, setShippingPreference] = useState('CLAIM')
  const shippingPreference = 'CLAIM'

  const [shoppingCartItems, setShoppingCartItems] = useState([])
  const [shoppingCartTotal, setShoppingCartTotal] = useState(0)

  const { cartItems, cartUser, clearCart } = useContext(CartContext)

  const [showRecentPaymentNotice, setShowRecentPaymentNotice] = useState(false)

  useEffect(() => {
    const lastPayment = JSON.parse(localStorage.getItem('vse-lastPaymentCompleted') || '{}')
    if (
      lastPayment.userEmail === cartUser?.email &&
      Date.now() - lastPayment.timestamp < 5 * 60 * 1000
    ) {
      clearCart()
      setShowRecentPaymentNotice(true)
    }
  }, [])

  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.CARD.key)

  const categoryIds = useMemo(
    () => cartItems.map((item) => item.categoryId),
    [cartItems],
  )

  const displayError = (message: string) => {
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 })
  }

  const { categories, apiLoading } = useCategoryFees({
    categoryIds,
    // onError: displayError,
  })

  const updateShoppingItems = useCallback(() => {
    const { items, total } = generateCartItems(
      cartItems,
      categories,
      paymentMode,
    )
    setShoppingCartItems(items)
    setShoppingCartTotal(total)
  }, [categories, cartItems, paymentMode])

  useEffect(() => {
    updateShoppingItems()
    setPageLoading(apiLoading)

    return () => {
      setPageLoading(false)
    }
  }, [updateShoppingItems, setPageLoading, apiLoading])

  const isSubmittingRef = useRef(false)

  const handleCheck = (e) => {
    if (e.target.name === 'waiverCB') setCheckWaiver(e.target.checked)
    if (e.target.name === 'waiverOrg') setCheckWaiverOrg(e.target.checked)
    if (e.target.name === 'agreeVerify') setCheckAgreeVerify(e.target.checked)
  }

  const errorCallback = (error: any) => {
    rollbar.critical(error)
    if (typeof error === 'object' && 'message' in error)
      displayError(error.message)
    else displayError(error)
  }

  const handleFreeSubmit = async () => {
    rollbar.info({
      cartUser,
      cartItems,
    })

    setLoading(true)
    setPageLoading(true)
    try {
      await doCreateFreeRegistration(cartUser, cartItems)
      setLoading(false)
      setPageLoading(false)
      window.location.href = `${window.location.origin}${process.env.REACT_APP_PAYMONGO_RETURN_URL}?free_registration`
    } catch (e) {
      setLoading(false)
      setPageLoading(false)
      errorCallback(e)
    }
  }

  const renderCheckout = () => {
    return (
      <>
        <PaymongoCheckoutForm
          shoppingCartTotal={shoppingCartTotal}
          setPaymentMode={setPaymentMode}
          initialValues={{
            paymentType: paymentMode,
            country: 'PH',
            privacyPolicy: false,
          }}
          displayError={(values) => displayError(`${values.message}`)}
          updateData={async (values) => {
            if (isSubmittingRef.current) {
              return [null, { message: 'Already processing your payment. Please wait.' }]
            }
            isSubmittingRef.current = true

            const idempotencyKey = uuidv4()

            try {
              rollbar.info({
                cartUser,
                cartItems,
                idempotencyKey,
              })

              let currentCategoryFees = await fetchCategoryFees(categoryIds)
              if (currentCategoryFees.length) currentCategoryFees = categories

              const { items: newCartItems, total: newShoppingCartTotal } =
                generateCartItems(cartItems, currentCategoryFees, paymentMode)

              if (
                dayjs().isSameOrAfter(
                  dayjs(
                    currentCategoryFees[newCartItems[0].categoryId]
                      .registrationCutOff,
                  ),
                )
              ) {
                isSubmittingRef.current = false
                return [
                  null,
                  {
                    title: 'Event unavailable',
                    message: 'Sorry. Event registration has ended.',
                  },
                ]
              }

              const metadata: { [key: string]: string } = {}
              newCartItems.forEach((item, idx) => {
                metadata[
                  `itemName${idx + 1}`
                ] = `${item.eventName} [${item.categoryName}] - ${item.shirtSize}`
                metadata[`itemAmount${idx + 1}`] =
                  (item.isTriathlon ||
                    Object.keys(TEAM_TYPES).includes(item.teamType)) &&
                  idx > 0
                    ? ''
                    : item.isBonus
                    ? 'FREE'
                    : `PHP ${item.fee.toFixed(2)}`
                metadata[`itemTransactionFee${idx + 1}`] =
                  (item.isTriathlon ||
                    Object.keys(TEAM_TYPES).includes(item.teamType)) &&
                  idx > 0
                    ? ''
                    : item.isBonus
                    ? 'FREE'
                    : `PHP ${item.adminFee.toFixed(2)}`
              })

              metadata['idempotencyKey'] = idempotencyKey

              rollbar.info('Payment step 1: Creating PaymentIntent', {
                idempotencyKey,
                eventName: newCartItems[0].eventName,
                amount: newShoppingCartTotal,
              })

              const piRes: PaymentIntentResponse = await createPaymentIntent(
                newCartItems[0].eventName,
                parseInt((newShoppingCartTotal * 100).toFixed()),
                metadata,
                idempotencyKey,
              )

              rollbar.info('Payment step 2: PaymentIntent created', {
                idempotencyKey,
                paymentIntentId: piRes.data.id,
              })

              const pmRes: PaymentMethodResponse = await createPaymentMethod({
                type: values.paymentType.toLowerCase(),
                billing: {
                  address: {
                    line1: values.addressLine1,
                    city: values.city,
                    state: values.state,
                    postal_code: values.postalCode,
                    country: values.country,
                  },
                  name: values.name,
                  email: values.email,
                  phone: values.phone,
                },
                ...(values.paymentType === PAYMENT_MODES.CARD.key && {
                  details: {
                    card_number: values.cardNumber.toString(),
                    exp_month: parseInt(values.expMonth.toString()),
                    exp_year: parseInt(values.expYear.toString()),
                    cvc: values.cvc.toString(),
                  },
                }),
              })

              rollbar.info('Payment step 3: PaymentMethod created', {
                idempotencyKey,
                paymentMethodId: pmRes.data.id,
              })

              if ('errors' in pmRes) {
                isSubmittingRef.current = false
                return [
                  null,
                  { message: `Payment error: ${pmRes.errors[0].detail}` },
                ]
              }

              const attachPayload: AttachPaymentIntentRequest = {
                payment_method: pmRes.data.id,
                client_key: piRes.data.attributes.client_key,
                ...([
                  PAYMENT_MODES.GCASH.key,
                  PAYMENT_MODES.PAYMAYA.key,
                  PAYMENT_MODES.GRAB_PAY.key,
                ].includes(values.paymentType) && {
                  return_url: `${window.location.origin}${process.env.REACT_APP_PAYMONGO_RETURN_URL}`,
                }),
              }

              const aRes: AttachPaymentIntentResponse =
                await attachToPaymentIntent(piRes.data.id, attachPayload)

              rollbar.info('Payment step 4: Attach response received', {
                idempotencyKey,
                createdPaymentIntentId: piRes.data.id,
                attachedPaymentIntentId: aRes.data.id,
                match: piRes.data.id === aRes.data.id,
              })

              if ('errors' in aRes) {
                isSubmittingRef.current = false
                return [
                  null,
                  {
                    title: 'Error encountered',
                    message:
                      'Sorry. Error encountered while processing request. Please contact admin@megatechph.com for assistance ',
                  },
                ]
              }

              if (aRes.data.id !== piRes.data.id) {
                rollbar.error('PaymentIntent ID mismatch after attach', {
                  idempotencyKey,
                  createdPaymentIntentId: piRes.data.id,
                  attachedPaymentIntentId: aRes.data.id,
                  cartUser,
                  cartItems,
                })
                isSubmittingRef.current = false
                return [
                  null,
                  {
                    title: 'Payment processing error',
                    message: 'Sorry. An error occurred while processing your payment. Please try again or contact admin@megatechph.com for assistance.',
                  },
                ]
              }

              await doCreatePendingOrder(
                cartUser,
                newCartItems,
                piRes.data.id,
                newShoppingCartTotal,
                piRes.data.attributes.status,
                values.paymentType,
                shippingPreference,
                idempotencyKey,
              )

              rollbar.info('Payment step 5: Order saved', {
                idempotencyKey,
                paymentIntentId: piRes.data.id,
              })

              if (aRes.data.attributes.next_action === null) {
                window.location.href = `${window.location.origin}${process.env.REACT_APP_PAYMONGO_RETURN_URL}?payment_intent_id=${aRes.data.id}`
              } else {
                if (
                  aRes.data.attributes.next_action.redirect?.return_url !== null
                )
                  window.location.href =
                    aRes.data.attributes.next_action.redirect.url
              }
            } catch (e) {
              isSubmittingRef.current = false
              errorCallback(e)
              return [null, e]
            }
          }}
          setLoading={(a: boolean) => {
            setLoading(a)
            setPageLoading(a)
          }}
        />
      </>
    )
  }

  const renderContent = () => {
    if (showRecentPaymentNotice)
      return (
        <Page centered>
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-block',
                textAlign: 'center',
                color: '#2e7d32',
                marginBottom: '24px',
              }}>
              <CheckCircleIcon sx={{ fontSize: '40px' }} />
              <Typography
                variant="h4"
                sx={{ color: '#2e7d32', ml: 1, display: 'inline-block', verticalAlign: 'top' }}>
                Processing Payment
              </Typography>
            </div>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Thank you for registering. We are currently processing your payment. An email
              confirmation will be sent to you shortly once your payment is received!
            </Typography>
            <Typography variant="caption">
              <strong>NOTE: </strong>If you can not find email in your inbox, please check your spam
              or junk folder. If you did not get any email confirmation, contact{' '}
              <a
                href="mailto:support@megatechph.com"
                style={{ textDecoration: 'none' }}>
                support@megatechph.com
              </a>{' '}
              to be safe on your side.
            </Typography>
            <div style={{ marginTop: 16 }}>
              <Link href="/events" sx={{ textDecoration: 'none' }}>
                <Button color="secondary" variant="contained">
                  BACK to Events
                </Button>
              </Link>
            </div>
          </div>
        </Page>
      )

    if (cartItems.length === 0)
      return (
        <Page centered>
          <EmptyState
            header={''}
            containerSx={{ textAlign: 'center' }}
            text={'Your shopping cart is empty.'}>
            <Link href="/events" sx={{ textDecoration: 'none' }}>
              <Button color="secondary" variant="contained">
                BACK to Events
              </Button>
            </Link>
          </EmptyState>
        </Page>
      )

    return (
      <>
        <div style={{ height: 48 }} />
        {loading && <OngoingPaymentNotice isFree={isFree} />}
        <Grid container spacing={5}>
          <RegistrationSummary
            shoppingCartItems={shoppingCartItems}
            shoppingCartTotal={shoppingCartTotal}
            loading={loading || pageLoading}
            checkWaiver={checkWaiver}
            checkWaiverOrg={checkWaiverOrg}
            checkAgreeVerify={checkAgreeVerify}
            handleCheck={handleCheck}
            isVirtualEvent={isVirtualEvent}
            eventWaiver={eventWaiver}
            onCancel={onCancel}
            isFree={isFree}
            handleFreeSubmit={async () => {
              setLoading(true)
              await handleFreeSubmit()
            }}
          />
          {!isFree && (
            <Grid
              sx={{ paddingTop: 0 }}
              size={{
                xs: 12,
                sm: 6,
              }}>
              {checkWaiver &&
                checkWaiverOrg &&
                checkAgreeVerify &&
                renderCheckout()}
            </Grid>
          )}
        </Grid>
      </>
    )
  }

  return <Container disableGutters>{show && renderContent()}</Container>
}

export default PaymongoCheckout
